import { useEffect } from "react";
import { getErrorBuffer } from "utils/errorBuffer";

// What a Help & feedback report sends automatically (docs/help-feedback-plan.md).
//
// Pages that know something worth sending — the open project and scenario, the wizard's draft —
// register it here with useReportContext(key, value); the dialog collects everything at Send.
// A module-level registry rather than React context so the dialog, which lives in the layout,
// needs nothing from the page tree, and so a page that forgets to register simply contributes
// nothing instead of breaking the report.
const registry = new Map();

export const setReportContext = (key, value) => {
  if (value === null || value === undefined) registry.delete(key);
  else registry.set(key, value);
};

export const useReportContext = (key, value) => {
  useEffect(() => {
    setReportContext(key, value);
    return () => registry.delete(key);
    // The caller passes a fresh object each render; compare by its JSON so a stable page does
    // not churn the registry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, JSON.stringify(value ?? null)]);
};

export const getRegisteredContext = () => Object.fromEntries(registry.entries());

// The build is identified by the main bundle's content hash — there is no build-id env var,
// and this changes on every deploy, which is all a report needs.
const buildId = () => {
  try {
    const script = [...document.querySelectorAll("script[src]")].map((s) => s.getAttribute("src")).find((src) => /\/main\.[a-f0-9]+\.js/.test(src || ""));
    const m = script && script.match(/main\.([a-f0-9]+)\.js/);
    return m ? m[1] : "dev";
  } catch {
    return "unknown";
  }
};

export const collectReportContext = ({ user, workspace } = {}) => {
  const registered = getRegisteredContext();
  const nav = typeof navigator !== "undefined" ? navigator : {};
  const scr = typeof window !== "undefined" ? window.screen : null;
  return {
    user: user ? { id: user._id || user.id, email: user.email, name: `${user.firstName || ""} ${user.lastName || ""}`.trim(), role: user.role } : null,
    workspace,
    page: {
      url: window.location.href,
      title: document.title,
      referrer: document.referrer || "",
    },
    app: { buildId: buildId(), locale: nav.language },
    browser: {
      userAgent: nav.userAgent,
      platform: nav.platform || nav.userAgentData?.platform || "",
      screen: scr ? `${scr.width}x${scr.height} @${window.devicePixelRatio || 1}x, viewport ${window.innerWidth}x${window.innerHeight}` : "",
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      online: nav.onLine,
    },
    ...registered,
    ...getErrorBuffer(),
  };
};
